import { Capability, Clonable, isManager, ServiceProvider, UserInviteStatus } from '../../..';

export enum UserTeamStatus {
  PendingInvite = 'pending-invite',
  NoTeam = 'no-team',
  Member = 'member',
  Manager = 'manager',
}

export class User extends Clonable {
  constructor(
    public readonly id: string,
    public readonly createdAt: Date,
    public readonly email: string,
    public readonly capabilities: Capability[],
    public readonly displayName?: string,
    public readonly givenName?: string,
    public readonly familyName?: string,
    public readonly firstLogin?: Date,
    public readonly lastLogin?: Date,
    public readonly lastLoginProvider?: ServiceProvider,
    public readonly lastLoginProviderUserId?: string,
    public readonly teamId?: string,
    public readonly teamInvite?: UserInviteStatus,
  ) {
    super();
  }

  public can(capability: Capability): boolean {
    return this.capabilities.includes(capability) || this.isAdmin || this.isOwner;
  }

  get displayNameAndOrEmail(): string {
    if (this.displayName) {
      return `${this.displayName} (${this.email})`;
    } else {
      return this.email;
    }
  }

  get fullName(): string | undefined {
    const fullName = `${this.givenName ?? ''} ${this.familyName ?? ''}`.trim();

    if (fullName !== '') {
      return fullName;
    }

    return undefined;
  }

  get hasDisplayName(): boolean {
    return !!this.displayName?.trim();
  }

  get isMember(): boolean {
    return this.capabilities.length === 0;
  }

  get isAdmin(): boolean {
    return this.capabilities.includes(Capability.Admin);
  }

  get isFeedManager(): boolean {
    return this.capabilities.includes(Capability.FeedManager);
  }

  get isFirstLogin(): boolean {
    return (
      !!this.firstLogin &&
      !!this.lastLogin &&
      this.firstLogin.getTime() === this.lastLogin.getTime()
    );
  }

  get isOwner(): boolean {
    return this.capabilities.includes(Capability.Owner);
  }

  get name(): string {
    return this.displayName ?? this.email;
  }

  get teamStatus(): UserTeamStatus {
    if (this.teamId) {
      if (this.teamInvite === UserInviteStatus.Pending) {
        return UserTeamStatus.PendingInvite;
      } else if (isManager(this.capabilities)) {
        return UserTeamStatus.Manager;
      } else {
        return UserTeamStatus.Member;
      }
    }

    return UserTeamStatus.NoTeam;
  }
}

export class FormerUser extends Clonable {
  constructor(
    public readonly id: string,
    public readonly removedAt: Date,
    public readonly email: string,
    public readonly displayName?: string,
  ) {
    super();
  }

  get name(): string {
    return this.displayName ?? this.email;
  }
}
